import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { capitalize } from 'lodash';
import { feeSchedulePrograms as fspUtils } from '@unite-us/client-utils';
import { Card } from 'common/Card';
import DollarAmount from 'common/display/Money/DollarAmount';
import { useFeatureFlag } from 'common/hooks';
import { AUTHORIZATION_STATUSES } from 'common/Status/authorizationConstants';
import formatDate from 'common/utils/Dates/formatDate';
import ServiceAuthorizationStatusIndicator from '../../../components/ServiceAuthorizationStatusIndicator';
import { DetailList, TwoPanelCardBody } from '../Layout';
import {
  AmountLineItem,
  CapInformationLineItem,
  ClinicalModificationsLineItem,
  ServiceDeliveryDatesLineItem,
  BaseLineItem,
} from '../LineItems';
import ValueWithStrikethroughChanges from '../ValueWithStrikethroughChanges';

const UnilaterallyUpdatedAuthorizationCard = ({ authorization }) => {
  const editReasonFeatureEnabled = useFeatureFlag('pays-10493-service-authorization-edit-reasons');
  const shortId = authorization.short_id;
  const hasClinicalModifications = authorization.clinical_modifications?.length > 0;
  const currentApprovedAmount = authorization.approved_cents || authorization.approved_unit_amount;
  const editReason = authorization.service_authorization_edit_reason?.reason || 'N/A';

  const isVoided = authorization.state === AUTHORIZATION_STATUSES.void;
  const showPreviousValues = !isVoided;

  const previousAuth = showPreviousValues ? authorization.previously_approved_auth : {};
  const previousApprovedAmount = previousAuth.approved_cents || previousAuth.approved_unit_amount;

  const isUnitBased = !fspUtils.isCostPaymentType(authorization.fee_schedule_program.payment_type);

  return (
    <Card
      borderColor={isVoided ? 'border-brand-blue' : 'border-green'}
      className="bg-white overflow-visible"
      data-testid="um-created-auth-details-card"
    >
      <div className="px-6 border-b border-dark-fill-blue flex justify-between">
        <div className="py-4">
          <h1 className="text-text-blue font-heavy-font text-2xl leading-8">
            Authorization #{shortId}
          </h1>
          <div className="flex space-x-6 items-center">
            <p className="text-text-blue text-sm leading-5">Last updated: {formatDate(authorization.updated_at)}</p>
            <ServiceAuthorizationStatusIndicator
              autoApproved={false}
              isUpdateRequest
              isUrgent={false}
              status={authorization.state}
            />
          </div>
        </div>
      </div>
      <TwoPanelCardBody
        firstPanel={(
          <DetailList>
            <AmountLineItem
              currentAmount={currentApprovedAmount}
              fieldName="Authorized Amount"
              isUnitBased={isUnitBased}
              previousAmount={previousApprovedAmount}
              showOriginalValues={showPreviousValues}
              unit={authorization.fee_schedule_program.unit}
            />
            <ServiceDeliveryDatesLineItem
              endDate={authorization.approved_ends_at}
              fieldName="Authorized Service Delivery Date(s)"
              previousEndDate={previousAuth.approved_ends_at}
              previousStartDate={previousAuth.approved_starts_at}
              showOriginalValues={showPreviousValues}
              startDate={authorization.approved_starts_at}
              testElement="service-delivery-dates"
            />
            <BaseLineItem field="Payer Authorization ID">
              <ValueWithStrikethroughChanges
                currentValue={authorization.payer_authorization_number || 'N/A'}
                previousValue={previousAuth.payer_authorization_number || 'N/A'}
                showOriginalValues={showPreviousValues}
              />
            </BaseLineItem>
            {hasClinicalModifications && (
              <ClinicalModificationsLineItem
                clinicalModifications={authorization.clinical_modifications}
                previousClinicalModifications={previousAuth.clinical_modifications}
                showOriginalValues={showPreviousValues}
              />
            )}
            {editReasonFeatureEnabled && (
              <BaseLineItem field="Reason for Editing Authorization">
                {editReason}
              </BaseLineItem>
            )}
            <BaseLineItem field="Notes">
              {authorization.reviewer_note || 'N/A'}
            </BaseLineItem>
          </DetailList>
        )}
        secondPanel={(
          <DetailList>
            <BaseLineItem field="Contracted Program">
              {authorization.fee_schedule_program.name}
            </BaseLineItem>
            <BaseLineItem field="Payment Approach">
              {fspUtils.PAYMENT_TYPES[authorization.fee_schedule_program.payment_type]}
            </BaseLineItem>
            {isUnitBased && (
              <>
                <BaseLineItem field="Unit">
                  {capitalize(authorization.fee_schedule_program.unit)}
                </BaseLineItem>
                <BaseLineItem field="Rate">
                  <DollarAmount
                    convertCents
                    value={authorization.fee_schedule_program.unit_rate}
                  />
                  {' '}
                  per {authorization.fee_schedule_program.unit}
                </BaseLineItem>
              </>
            )}
            <CapInformationLineItem capInformation={authorization.fee_schedule_program.cap_information} />
            {showPreviousValues && (
              <BaseLineItem field="Previous Authorization">
                <Link
                  className="text-action-blue"
                  to={`/authorizations/${authorization.previously_approved_auth.id}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {authorization.previously_approved_auth.short_id}
                </Link>
              </BaseLineItem>
            )}
          </DetailList>
        )}
      />
    </Card>
  );
};

UnilaterallyUpdatedAuthorizationCard.propTypes = {
  authorization: PropTypes.object.isRequired,
};

export default UnilaterallyUpdatedAuthorizationCard;
